import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import type { PostingStatus } from '@pms/shared';
import {
  fetchPropertyImages,
  createPropertyImage,
  updatePropertyImage,
  deletePropertyImage,
  setCoverImage,
  fetchPostings,
  createPosting,
  updatePosting,
  deletePosting,
  publishPosting,
  archivePosting,
  fetchClientMedia,
  createClientMedia,
  updateClientMedia,
  deleteClientMedia,
  approveClientMedia,
  rejectClientMedia,
  type UpsertPropertyImageBody,
  type CreatePostingBody,
  type UpdatePostingBody,
  type CreateClientMediaBody,
  type UpdateClientMediaBody,
} from '@/services/api/content.api';

const CONTENT_KEY = ['content'] as const;

export const contentKeys = {
  images: (propertyId: string) =>
    [...CONTENT_KEY, 'images', propertyId] as const,
  postings: (status: PostingStatus | 'all') =>
    [...CONTENT_KEY, 'postings', status] as const,
  media: (propertyId: string) =>
    [...CONTENT_KEY, 'media', propertyId] as const,
};

// ── Property images ─────────────────────────────────────────────────────────

export function usePropertyImages(propertyId: string | null) {
  return useQuery({
    queryKey: contentKeys.images(propertyId ?? ''),
    queryFn: () => fetchPropertyImages(propertyId as string),
    enabled: !!propertyId,
    staleTime: 15_000,
  });
}

function useInvalidateImages(propertyId: string) {
  const queryClient = useQueryClient();
  return () =>
    void queryClient.invalidateQueries({
      queryKey: contentKeys.images(propertyId),
    });
}

export function useCreatePropertyImage(propertyId: string) {
  const invalidate = useInvalidateImages(propertyId);
  return useMutation({
    mutationFn: (body: UpsertPropertyImageBody) =>
      createPropertyImage(propertyId, body),
    onSuccess: () => invalidate(),
  });
}

export function useUpdatePropertyImage(propertyId: string) {
  const invalidate = useInvalidateImages(propertyId);
  return useMutation({
    mutationFn: ({
      id,
      patch,
    }: {
      id: string;
      patch: Partial<UpsertPropertyImageBody>;
    }) => updatePropertyImage(id, patch),
    onSuccess: () => invalidate(),
  });
}

export function useDeletePropertyImage(propertyId: string) {
  const invalidate = useInvalidateImages(propertyId);
  return useMutation({
    mutationFn: (id: string) => deletePropertyImage(id),
    onSuccess: () => invalidate(),
  });
}

export function useSetCoverImage(propertyId: string) {
  const invalidate = useInvalidateImages(propertyId);
  return useMutation({
    mutationFn: (id: string) => setCoverImage(id),
    onSuccess: () => invalidate(),
  });
}

// ── Postings ────────────────────────────────────────────────────────────────

export function usePostings(status: PostingStatus | 'all') {
  return useQuery({
    queryKey: contentKeys.postings(status),
    queryFn: () => fetchPostings(status === 'all' ? undefined : status),
    staleTime: 15_000,
  });
}

function useInvalidatePostings() {
  const queryClient = useQueryClient();
  return () =>
    void queryClient.invalidateQueries({
      queryKey: [...CONTENT_KEY, 'postings'],
    });
}

export function useCreatePosting() {
  const invalidate = useInvalidatePostings();
  return useMutation({
    mutationFn: (body: CreatePostingBody) => createPosting(body),
    onSuccess: () => invalidate(),
  });
}

export function useUpdatePosting() {
  const invalidate = useInvalidatePostings();
  return useMutation({
    mutationFn: ({ id, patch }: { id: string; patch: UpdatePostingBody }) =>
      updatePosting(id, patch),
    onSuccess: () => invalidate(),
  });
}

export function useDeletePosting() {
  const invalidate = useInvalidatePostings();
  return useMutation({
    mutationFn: (id: string) => deletePosting(id),
    onSuccess: () => invalidate(),
  });
}

export function usePublishPosting() {
  const invalidate = useInvalidatePostings();
  return useMutation({
    mutationFn: (id: string) => publishPosting(id),
    onSuccess: () => invalidate(),
  });
}

export function useArchivePosting() {
  const invalidate = useInvalidatePostings();
  return useMutation({
    mutationFn: (id: string) => archivePosting(id),
    onSuccess: () => invalidate(),
  });
}

// ── Client media ──────────────────────────────────────────────────────────

export function useClientMedia(propertyId: string | null) {
  return useQuery({
    queryKey: contentKeys.media(propertyId ?? ''),
    queryFn: () => fetchClientMedia(propertyId as string),
    enabled: !!propertyId,
    staleTime: 15_000,
  });
}

function useInvalidateMedia(propertyId: string) {
  const queryClient = useQueryClient();
  return () =>
    void queryClient.invalidateQueries({
      queryKey: contentKeys.media(propertyId),
    });
}

export function useCreateClientMedia(propertyId: string) {
  const invalidate = useInvalidateMedia(propertyId);
  return useMutation({
    mutationFn: (body: CreateClientMediaBody) =>
      createClientMedia(propertyId, body),
    onSuccess: () => invalidate(),
  });
}

export function useUpdateClientMedia(propertyId: string) {
  const invalidate = useInvalidateMedia(propertyId);
  return useMutation({
    mutationFn: ({ id, patch }: { id: string; patch: UpdateClientMediaBody }) =>
      updateClientMedia(id, patch),
    onSuccess: () => invalidate(),
  });
}

export function useDeleteClientMedia(propertyId: string) {
  const invalidate = useInvalidateMedia(propertyId);
  return useMutation({
    mutationFn: (id: string) => deleteClientMedia(id),
    onSuccess: () => invalidate(),
  });
}

export function useApproveClientMedia(propertyId: string) {
  const invalidate = useInvalidateMedia(propertyId);
  return useMutation({
    mutationFn: (id: string) => approveClientMedia(id),
    onSuccess: () => invalidate(),
  });
}

export function useRejectClientMedia(propertyId: string) {
  const invalidate = useInvalidateMedia(propertyId);
  return useMutation({
    mutationFn: (id: string) => rejectClientMedia(id),
    onSuccess: () => invalidate(),
  });
}
